import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { nosPushKeys } from "@/lib/query";
import type { NosPushChange, NosPushStatus, NosPushView } from "@/schemas/nos-push";

export const NOS_PUSH_PAGE_SIZE = 25;

/**
 * One outbound person push to NOS 1.0, joined with the person's name for
 * display. (Backend twin: api/nos-push.ts `queryNosPushes`.)
 */
export interface NosPushRow {
  id: string;
  personId: string;
  personName: string;
  status: NosPushStatus;
  attempts: number;
  lastError: string | null;
  /**
   * The mapped fields this push changes, with values already resolved to
   * display form server-side (person names, not uuids). Empty for rows queued
   * before the pre-edit state was recorded.
   */
  changes: NosPushChange[];
  nextRetryAt: string | null;
  createdAt: string;
  sentAt: string | null;
}

interface NosPushPage {
  data: NosPushRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface NosPushPageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

export function useNosPushes(filters: { view: NosPushView }, pageRequest: NosPushPageRequest) {
  return useQuery({
    queryKey: [...nosPushKeys.list(filters), pageRequest],
    queryFn: async (): Promise<NosPushPage> => {
      const params = new URLSearchParams();
      params.set("limit", String(NOS_PUSH_PAGE_SIZE));
      params.set("view", filters.view);
      if (pageRequest.cursor) params.set("cursor", pageRequest.cursor);
      params.set("direction", pageRequest.direction);
      const res = await fetch(`/api/nos-push?${params}`);
      if (!res.ok) throw new Error("Failed to load NOS sync status");
      return res.json();
    },
  });
}

/**
 * Retry a failed push. Invalidates the whole nos-push tree — a retried row
 * moves out of the failed set (back to pending), so both the Open list and
 * this row's own `attempts`/`lastError` are stale.
 */
export function useRetryNosPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/nos-push/${id}/retry`, { method: "POST" });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => null);
        const message =
          body !== null &&
          typeof body === "object" &&
          "error" in body &&
          typeof body.error === "string"
            ? body.error
            : "Failed to retry the push";
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: nosPushKeys.all });
      toast.success("Queued for retry");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to retry the push"),
  });
}

/**
 * One outbound PERSON HISTORY push. (Backend twin: api/nos-history-push.ts
 * `queryHistoryPushes`.) Carries a rendered span rather than a field diff —
 * the question this screen answers is "is NOS's history right?", not "which
 * fields changed".
 */
export interface NosHistoryPushRow {
  id: string;
  /** Null when the person row has since gone; the push is still shown. */
  personName: string | null;
  /**
   * Which history series — "manager" or "role classification". Two now write to
   * NOS, so a row without this is ambiguous. Server-rendered, so a third series
   * needs no client change.
   */
  series: string;
  operation: "upsert" | "deactivate";
  status: NosPushStatus;
  attempts: number;
  lastError: string | null;
  span: string;
  nextRetryAt: string | null;
  createdAt: string;
  sentAt: string | null;
}

interface NosHistoryPushPage {
  data: NosHistoryPushRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export function useNosHistoryPushes(
  filters: { view: NosPushView },
  pageRequest: NosPushPageRequest,
) {
  return useQuery({
    queryKey: [...nosPushKeys.historyList(filters), pageRequest],
    queryFn: async (): Promise<NosHistoryPushPage> => {
      const params = new URLSearchParams();
      params.set("limit", String(NOS_PUSH_PAGE_SIZE));
      params.set("view", filters.view);
      if (pageRequest.cursor) params.set("cursor", pageRequest.cursor);
      params.set("direction", pageRequest.direction);
      const res = await fetch(`/api/nos-push/history?${params}`);
      if (!res.ok) throw new Error("Failed to load NOS history sync status");
      return res.json();
    },
  });
}

export function useRetryNosHistoryPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/nos-push/history/${id}/retry`, { method: "POST" });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => null);
        const message =
          body !== null &&
          typeof body === "object" &&
          "error" in body &&
          typeof body.error === "string"
            ? body.error
            : "Failed to retry the push";
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: nosPushKeys.all });
      toast.success("Queued for retry");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to retry the push"),
  });
}
